const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
let Dexie = require('dexie'); // Must be let not const due to the if on line 6.
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { normalizeBarcode } = require('@/lib/Barcodes');

// See https://github.com/dfahlander/Dexie.js/issues/658
if (Dexie.default) {
    Dexie = Dexie.default;
}

/**
 * @constructor
 */
const DataStore = function () {
    /**
     * This is null until init() is called.
     *
     * @type {Dexie}
     */
    this.db = null;

    this.log = logFactory('DataStore');

    /**
     * Store names are stored here so they can be easily overridden during tests.
     * This matters when calling clearAll in a test because it will try and clear all the defined stores.
     *
     * @private
     *
     * @type {Object<string, string>}
     */
    this.STORE_NAMES = DataStoreNames;

    /**
     * @see http://dexie.org/docs/Dexie/Dexie.transaction()
     * @see http://dexie.org/docs/Dexie/Dexie.transaction()#specify-reusage-of-parent-transaction
     */
    this.TRANSACTION_MODE = {
        READWRITE: 'rw!',
        READONLY: 'r!',
    };
};

DataStore.prototype = {
    /**
     * Creates the Dexie database and defines its schema. This must be called before anything else.
     *
     * @param {string} [dbName] The name of the IndexedDB database.
     *                          Tests pass their own name so each one gets a database of its own.
     */
    init(dbName = 'propoint') {
        /**
         * @type {Dexie}
         */
        this.db = new Dexie(dbName);

        // Define the object stores and indexes.
        this.db.version(1).stores(
            {
                // Object store named 'sessions'. Primary key is 'ID'. 'eventID' is an extra index.
                sessions: 'ID,eventID',
                syncMetadata: 'name',
                test: 'name',
            }
        );

        this.db.version(2).stores(
            {
                marketingPreferences: 'ID',
                sessions: 'ID,eventID',
                syncMetadata: 'name',
                test: 'name',
                userSessions: 'ID,username',
            }
        );

        this.db.version(3).stores(
            {
                marketingPreferences: 'ID,status',
                sessions: 'ID,eventID',
                syncMetadata: 'name',
                test: 'name',
                userSessions: 'ID,username',
            }
        );

        this.db.version(4).stores(
            {
                categories: 'ID,status',
                marketingPreferences: 'ID,status',
                sessions: 'ID,eventID',
                syncMetadata: 'name',
                test: 'name',
                userSessions: 'ID,username',
            }
        );

        this.db.version(5).stores(
            {
                categories: 'ID,status',
                customFields: 'ID,level',
                giftVouchers: 'ID,catID,status',
                marketingPreferences: 'ID,status',
                membershipPlans: 'ID,catID,status',
                offers: 'ID,trigger',
                orders: 'ID,bookingRef',
                privileges: 'ID,userID',
                products: 'ID,catID,barcode,status',
                returnReasons: 'ID',
                sessions: 'ID,eventID',
                syncMetadata: 'name',
                templates: 'name',
                test: 'name',
                tickets: 'ID,catID,status',
                userSessions: 'ID,username',
            }
        );

        this.db.version(6).stores(
            {
                customFields: null, // customFields dropped to rename it to fields.
                privileges: null, // privileges dropped to recreate it with a new PK.
                templates: null, // templates dropped to recreate it with a new PK.
            }
        );

        this.db.version(7).stores(
            {
                fields: 'ID,level', // customFields recreated with new name of fields
                privileges: 'userID', // privileges recreated with userID as PK
                templates: '[name+printDriverRef]', // templates recreated with compound PK
            }
        );

        this.db.version(8).stores(
            {
                fields: 'ID,level,status', // Add status to fields so we can filter
            }
        );

        this.db.version(9).stores(
            {
                // Use an incrementing field for the order of items in the queue. Anything new goes to the end of the
                // queue. Initially I was worried about the maximum value as it is a counter that always goes up,
                // however https://w3c.github.io/IndexedDB/#key-generator-construct says:
                // If you generate a new key 1000 times per second day and night,
                // you won’t run into this limit for over 285000 years.
                onlineTasks: '++ID,type,status',
            }
        );

        this.db.version(10).stores(
            {
                // Drop syncMetadata
                syncMetadata: null,
            }
        );

        this.db.version(11).stores(
            {
                // Add eventID+startTimeYM compound index to sessions.
                sessions: 'ID,eventID,[eventID+startTimeYM],[eventID+startTimeYMD]',
            }
        );

        this.db.version(12).stores(
            {
                itemInstanceOrderMap: 'itemInstanceRef',
            }
        );

        this.db.version(13).stores(
            {
                sessionEvents: 'ID',
            }
        );

        this.db.version(14).stores(
            {
                // Add startTimeYMD index to sessions so they can be loaded for the today view.
                sessions: 'ID,eventID,[eventID+startTimeYM],[eventID+startTimeYMD],startTimeYMD',
            }
        );

        this.db.version(15).stores(
            {
                // Add branchIDs indexes to item tables.
                // This is a MultiEntry index because branchIDs is an array:
                // https://github.com/dexie/Dexie.js/issues/871#issuecomment-513874791
                // https://dexie.org/docs/MultiEntry-Index
                giftVouchers: 'ID,catID,*branchIDs',
                membershipPlans: 'ID,catID,*branchIDs',
                products: 'ID,catID,barcode,*branchIDs',
                tickets: 'ID,catID,*branchIDs',
                // [branchID+eventID+startTimeYM] is used to load sessions in the session picker.
                // [branchID+eventID+startTimeYMD] is used to load a list of days for an event in the session picker.
                // [branchID+startTimeYMD] is used by the today screen.
                sessions: 'ID,eventID,[branchID+eventID+startTimeYM],[branchID+eventID+startTimeYMD],[branchID+startTimeYMD]',
            }
        );

        this.db.version(16).stores(
            {
                images: 'key',
            }
        );

        this.db.version(17).stores(
            {
                giftAidRates: 'ID',
            }
        );

        this.db.version(18).stores(
            {
                // Add a new table for kitchen zones.
                // Indexes are the ID and branchID.
                [DataStoreNames.KITCHEN_ZONES]: 'ID,branchID',
            }
        );

        this.db.version(19).stores(
            {
                [DataStoreNames.ITEM_OPTIONS]: 'ID',
                [DataStoreNames.OPTION_SETS]: 'ID,*itemIDs,status',
            }
        );

        this.db.version(20).stores(
            {
                // An item can have several barcodes, and the same barcode can be used by several items,
                // so a barcode potentially identifies a set of items rather than one.
                // itemType is stored on the row but not indexed, it's only needed to load the item.
                [DataStoreNames.ITEM_BARCODES]: '[itemID+barcode],barcode',

                // Drop the barcode index from products.
                products: 'ID,catID,*branchIDs',
            }
        ).upgrade(async (tx) => {
            // Fill the new barcodes from the single barcode each product used to have.

            // Load all existing products from the DB.
            const products = await tx.table(DataStoreNames.PRODUCTS).toArray();

            // Build a list of barcodes to insert.
            const barcodeRows = [];

            products.forEach((product) => {
                // Move it to the barcodeS property.
                product.barcodes = product.barcode ? [normalizeBarcode(product.barcode)] : [];

                // Create a barcode row for it.
                product.barcodes.forEach((barcode) => {
                    barcodeRows.push({
                        itemID: product.ID,
                        itemType: product.itemType,
                        barcode,
                    });
                });

                delete product.barcode;
            });

            await tx.table(DataStoreNames.PRODUCTS).bulkPut(products);
            await tx.table(DataStoreNames.ITEM_BARCODES).bulkPut(barcodeRows);
        });

        // If you change the structure of the above you must create a new version with an incremented version number,
        // and all the previous version definitions must remain in this file!
        // Note that you do not need to re-state the entire DB definition in each version (as was thought in the
        // earlier versions listed here) you only need to state the changes.
        // See: https://dexie.org/docs/Tutorial/Design#database-versioning
        // e.g.
        // this.db.version(4).stores(
        //    {
        //        newTable: 'ID',
        //    }
        // );
        // Also update the DataStoreNames enum object with the latest table names.
    },

    /**
     * @param {string} storeName
     *
     * @return {Dexie.Table}
     */
    getStore: function getStore(storeName) {
        return this.db.table(storeName);
    },

    /**
     * @param {string} storeName
     * @param {object} data
     *
     * @return {Promise}
     */
    async persist(storeName, data) {
        // We sometimes get an IndexedDB error when trying to store something that contains circular references
        // or other weirdness. This can be hard to debug as it doesn't tell us what caused the error!
        // To help with debugging these we can attempt to convert it to JSON here and if that fails we know saving it
        // in IndexedDB will fail.
        // Note that there may still be other reasons persisting in IndexedDB will fail, and this is helping with one
        // reason.
        try {
            JSON.stringify(data);
        } catch (e) {
            this.log.error(`Failed to convert to JSON to persist to store '${storeName}'`, data);
            console.trace();
            throw e;
        }

        try {
            // In Vue 3 reactive objects are Proxy objects, and these can't be stored in IndexedDB.
            // See https://digitickets.atlassian.net/browse/PROP-729
            // See https://github.com/dexie/Dexie.js/issues/1237
            data = Dexie.deepClone(data);
        } catch (e) {
            this.log.error(`Failed to clone data to persist to store '${storeName}'`, data);
            console.trace();
            throw e;
        }

        try {
            return await this.getStore(storeName).put(data);
        } catch (err) {
            this.log.error('Failed to persist data to store', storeName, data, err);
            throw err;
        }
    },

    /**
     * @param storeName
     * @param dataArray
     *
     * @return {Promise}
     */
    async persistMany(storeName, dataArray) {
        try {
            return await this.getStore(storeName).bulkPut(dataArray);
        } catch (err) {
            this.log.error('Failed to persist many data to store', storeName, dataArray, err);
            throw err;
        }
    },

    /**
     * @param storeName
     * @param keys
     * @return {Promise}
     */
    removeMany: function removeMany(storeName, keys) {
        return this.getStore(storeName).bulkDelete(keys);
    },

    /**
     * Find a single entity by its primary key.
     *
     * @param {string} storeName
     * @param {*} key
     *
     * @return {Dexie.Promise<Object>}
     */
    find: function find(storeName, key) {
        return this.getStore(storeName).get(key);
    },

    /**
     * Find many entities where the specified field has the specified value.
     * Note the field must be indexed.
     *
     * @param {string} storeName
     * @param {string} field
     * @param {*} value
     *
     * @return {Promise}
     */
    findWhere: function findWhere(storeName, field, value) {
        // let started = new Date();
        let where = {};
        where[field] = value;
        return this.getStore(storeName)
            .where(where)
            .toArray()
            .then((data) => {
                // console.debug(
                //     `Loaded ${data.length} items from ${storeName} where ${field} = ${value}`,
                //     `in ${(new Date()).getTime() - started.getTime()}ms`
                // );
                return data;
            });
    },

    /**
     * Return all items from a table.
     *
     * @param {string} storeName
     *
     * @return {Promise<[]>}
     */
    findAll: function findAll(storeName) {
        // let started = new Date();
        return this.getStore(storeName)
            .toArray()
            .then((data) => {
                // console.debug(
                //     `Loaded ${data.length} items from ${storeName}`,
                //     `in ${(new Date()).getTime() - started.getTime()}ms`
                // );
                return data;
            });
    },

    /**
     * Empty the specified store / table.
     *
     * @param {string} storeName
     *
     * @returns {Promise<void>}
     */
    clear: function clear(storeName) {
        return this.getStore(storeName).clear();
    },

    /**
     *
     * @returns {Promise<[]>}
     */
    clearAll: function clearAll() {
        let promises = [];
        const names = Object.values(this.STORE_NAMES);
        for (let i = 0; i < names.length; i++) {
            promises.push(this.clear(names[i]));
        }

        return Promise.all(promises);
    },

    /**
     * @param {string} mode
     * @param {array} tables
     * @param {function} callback
     *
     * @return {Promise}
     */
    transaction: function transaction(mode, tables, callback) {
        return this.db.transaction(
            mode,
            tables,
            callback
        );
    },

    dexieMinKey() {
        return Dexie.minKey;
    },

    dexieMaxKey() {
        return Dexie.maxKey;
    },

    deleteDB() {
        return Dexie.delete();
    },
};

module.exports = DataStore;
